<template>
  <a
    class="button-social"
    :class="buttonSocialDesignObject"
    :page-url="page_url"
    :page-title="page_title"
    :page-description="page_description"
    :page-image="page_image"
    :button-design="button_design"
    :title-social="title_social"
    :has-square-edges="has_square_edges"
    :has-icon="has_icon"
    :has-counter="has_counter"
    @click.prevent="showShareWindow"
  >
    <i class="icon-vkontakte" v-if="this.$props.has_icon"></i>
    <span class="title-social" v-if="this.$props.title_social">
      {{ title_social }}
    </span>
    <span
      class="counter-vkontakte"
      v-model="counter_vkontakte"
      v-if="this.$props.has_counter"
    >
      {{ counter_vkontakte }}
    </span>
  </a>
</template>

<script>
import { clickEvent } from "../helpers/events";
import { documentHref } from "../helpers/href";
import { documentTitle } from "../helpers/title";
import { metaDescription } from "../helpers/description";
import { linkAppleTouchIcon } from "../helpers/icon";
import { sliceThousandInt } from "../helpers/count_number";
import { getRandomInt } from "../helpers/random_int";
import { openPopUpWindow } from "../helpers/popup_window";

export default {
  name: "VueGoodshareVkontakte",
  props: {
    page_url: {
      type: String,
      default: documentHref
    },
    page_title: {
      type: String,
      default: documentTitle
    },
    page_description: {
      type: String,
      default: metaDescription
    },
    page_image: {
      type: String,
      default: linkAppleTouchIcon
    },
    button_design: {
      type: String,
      default: () => "flat"
    },
    title_social: String,
    has_icon: Boolean,
    has_square_edges: Boolean,
    has_counter: Boolean
  },
  data() {
    return {
      buttonSocialDesignObject: {
        "button-social__square_edges": this.$props.has_square_edges,
        vkontakte__design__flat: this.$props.button_design === "flat",
        vkontakte__design__gradient: this.$props.button_design === "gradient",
        vkontakte__design__outline: this.$props.button_design === "outline"
      },
      counter_vkontakte: 0
    };
  },
  methods: {
    /**
     * Show share window.
     *
     * @return {object} a pop-up window
     */
    showShareWindow: function() {
      // Variables
      const width = 640;
      const height = 480;
      const share_url = `https://vk.com/share.php?url=${encodeURIComponent(
        this.$props.page_url
      )}&title=${encodeURIComponent(
        this.$props.page_title
      )}&description=${encodeURIComponent(
        this.$props.page_description
      )}&image=${encodeURIComponent(this.$props.page_image)}&noparse=true`;

      // onClick event
      clickEvent(this, "vkontakte");

      return openPopUpWindow(share_url, width, height);
    },

    handleUpdateCount(count) {
      this.counter_vkontakte = count >= 1000 ? sliceThousandInt(count) : count;
    },

    /**
     * Get share counter.
     *
     * @return {object} a share counter
     */
    getShareCounter: function() {
      // Let's see whether some other component has already
      // asked for count. Then we just subscribe for the count update event
      if (
        window.VK &&
        window.VK.Share &&
        typeof window.VK.Share.count === "function"
      ) {
        return;
      }
      // Variables
      const script = document.createElement("script");

      // Create `script` tag with share count URL
      script.src = `https://vk.com/share.php?act=count&index=${getRandomInt(
        1,
        2345
      )}&url=${encodeURIComponent(this.$props.page_url)}`;

      // Add `script` tag with share count URL
      // to end of `body` tag
      document.body.appendChild(script);

      // Set share count to `counter_vkontakte` v-model
      window.VK = Object.assign({}, { Share: {} }, window.VK);
      window.VK.Share.count = (index, count) => {
        if (count) this.$root.$emit("VK:Share:count:update", count);
      };
    }
  },
  mounted() {
    // Show share counter when page loaded
    if (this.$props.has_counter) this.getShareCounter();
    // Subscribe to update count event using $root as an event bus
    this.$root.$on("VK:Share:count:update", this.handleUpdateCount.bind(this));
  },
  destroyed() {
    this.$root.$off("VK:Share:count:update", this.handleUpdateCount.bind(this));
  }
};
</script>

<style scoped lang="scss">
// Fontello
@font-face {
  font-family: "Fontello";
  src: url("../fonts/fontello.eot?26533562");
  src: url("../fonts/fontello.eot?26533562#iefix") format("embedded-opentype"),
    url("../fonts/fontello.woff2?26533562") format("woff2"),
    url("../fonts/fontello.woff?26533562") format("woff"),
    url("../fonts/fontello.ttf?26533562") format("truetype"),
    url("../fonts/fontello.svg?26533562#fontello") format("svg");
  font-weight: normal;
  font-style: normal;
}

[class^="icon-"]:before,
[class*=" icon-"]:before {
  font-family: "Fontello";
  font-style: normal;
  font-weight: normal;
  speak: none;
  display: inline-block;
  text-decoration: inherit;
  width: 1em;
  margin-right: 0.2em;
  text-align: center;
  font-variant: normal;
  text-transform: none;
  line-height: 1em;
  margin-left: 0.2em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.icon-vkontakte:before {
  content: "\e800";
}

// Colors
$vkontakte_main_color: rgb(76, 117, 163);
$vkontakte_main_color_opacity: rgba(76, 117, 163, 0.5);
$gradient_color: rgb(106, 147, 193);
$background_white_color: rgb(254, 254, 254);
$text_white_color: rgb(254, 254, 254);
$text_white_color_opacity: rgba(254, 254, 254, 0.5);

// Reset
.button-social * {
  box-sizing: border-box;
}

// Button Social link style
.button-social {
  display: inline-flex;
  cursor: pointer;
  padding: 7px 10px;
  margin: 3px 1.5px;
  border-radius: 3px;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
}

// Button Social link style on hover
.button-social:hover {
  opacity: 0.9;
}

// Button Social round edges
.button-social__square_edges {
  border-radius: 0;
  -moz-border-radius: 0;
  -webkit-border-radius: 0;
}

// Button Vkontakte style `flat`
.vkontakte__design__flat {
  background-color: $vkontakte_main_color;
  color: $text_white_color;
}

// Button Vkontakte style `gradient`
.vkontakte__design__gradient {
  background-image: linear-gradient(
    to bottom,
    $vkontakte_main_color,
    $gradient_color
  );
  background-image: -moz-linear-gradient(
    to bottom,
    $vkontakte_main_color,
    $gradient_color
  );
  background-image: -o-linear-gradient(
    to bottom,
    $vkontakte_main_color,
    $gradient_color
  );
  background-image: -webkit-linear-gradient(
    to bottom,
    $vkontakte_main_color,
    $gradient_color
  );
  background-image: -ms-linear-gradient(
    to bottom,
    $vkontakte_main_color,
    $gradient_color
  );
  color: $text_white_color;
}

// Button Vkontakte style `outline`
.vkontakte__design__outline {
  background-color: $background_white_color;
  border: 1px solid $vkontakte_main_color;
  color: $vkontakte_main_color;
}

// Title
.title-social {
  margin-left: 5px;
}

// Counter
.counter-vkontakte {
  margin-left: 6px;
  padding-left: 6px;
}

.vkontakte__design__flat .counter-vkontakte,
.vkontakte__design__gradient .counter-vkontakte {
  border-left: 1px solid $text_white_color_opacity;
}

.vkontakte__design__outline .counter-vkontakte {
  border-left: 1px solid $vkontakte_main_color_opacity;
}
</style>
